"use client";

import Link from "next/link";
import { useState } from "react";
import { GccIndiaMap, GCC_MAP_CITIES } from "@/components/GccIndiaMap";
import { citySlug } from "@/lib/site";

/** The widest label. It sizes the panel permanently — see the comment there —
 *  because the longest name is what wraps the preview onto the most lines. */
const LONGEST = GCC_MAP_CITIES.reduce((a, b) => (b.name.length > a.name.length ? b : a));

/** The India map beside its city panel, with the hover the copy promises.
 *
 *  The panel told the reader to "hover a city to preview it" while nothing
 *  was wired to hover at all — the map's pills only changed their own colour.
 *  Holding the hovered city here lets the map and the panel react together:
 *  hovering either side highlights the matching marker and swaps the panel to
 *  that city.
 *
 *  Hover is a preview, not a mode — pointer-only, so it is paired with focus
 *  for keyboard users, and the chips stay links so the map works untouched on
 *  a touchscreen, where nothing hovers at all.
 */
export function GccFootprint({
  intro,
  children,
  footer,
}: {
  /** The panel's resting copy, shown until a city is picked out. */
  intro: React.ReactNode;
  /** Heading + body above the panel, rendered on the server. */
  children: React.ReactNode;
  /** Rendered under the panel, inside the right column. */
  footer?: React.ReactNode;
}) {
  const [active, setActive] = useState<string | null>(null);
  const city = GCC_MAP_CITIES.find((c) => c.name === active) ?? null;

  return (
    <div className="grid items-start gap-12 lg:grid-cols-[560fr_528fr] lg:gap-16">
      <GccIndiaMap active={active} onHover={setActive} />

      <div>
        {children}

        {/* select-none because the panel's copy is replaced under the
            pointer as the reader moves across the chips. Dragging even
            slightly during that leaves the browser painting a selection
            across text that is no longer the text it started on. */}
        <div className="mt-10 select-none rounded-[10px] border border-brand-line bg-white p-6 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
          {/* Every state is stacked in ONE grid cell and toggled with
              visibility, so the box is permanently as tall as the tallest of
              them and swapping cannot move anything.

              A min-height was not enough and caused a feedback loop: the
              preview (name + two lines) is taller than the resting copy, so
              hovering grew the panel, which pushed the chips DOWN and out
              from under the pointer — mouseleave, revert, chips spring back
              up, mouseenter, and round again.

              Reserving that height only while no city was active fixed the
              growth and left the opposite fault: the box sat at its maximum
              at rest and SHRANK to whatever the hovered city needed. A short
              name wraps the sentence onto fewer lines than the longest one —
              measured on the live page, resting 242px against 209px for Pune.
              Moving across the map flipped it 33px each way, which is the
              jumping.

              So the reservation is permanent: a sizer carrying the longest
              name, never visible, never removed. The row is then max(sizer,
              copy, preview) — a constant — whatever is being shown. */}
          <div className="grid">
            <p
              aria-hidden
              className="invisible col-start-1 row-start-1 text-base font-light leading-relaxed"
            >
              <span className="font-medium">{LONGEST.name}</span>
              <br />
              Open the {LONGEST.name} map and Table Space micro-markets.
            </p>
            <div className={`col-start-1 row-start-1 ${city ? "invisible" : ""}`} aria-hidden={!!city}>
              {intro}
            </div>
            <p
              className={`col-start-1 row-start-1 text-base font-light leading-relaxed text-black ${city ? "" : "invisible"}`}
              aria-hidden={!city}
            >
              <span className="font-medium text-brand-accent">{(city ?? LONGEST).name}</span>
              <br />
              Open the {(city ?? LONGEST).name} map and Table Space micro-markets.
            </p>
          </div>

          <div className="mt-5 flex flex-wrap gap-1.5">
            {GCC_MAP_CITIES.map((c) => (
              <Link
                key={c.name}
                href={`/locations/${citySlug(c.name)}`}
                onMouseEnter={() => setActive(c.name)}
                onMouseLeave={() => setActive((p) => (p === c.name ? null : p))}
                onFocus={() => setActive(c.name)}
                onBlur={() => setActive((p) => (p === c.name ? null : p))}
                className={`select-none rounded-full border px-3 py-1.5 text-sm font-normal transition-colors ${
                  active === c.name
                    ? "border-brand-accent bg-brand-accent text-white"
                    : "border-brand-line text-black hover:border-brand-accent hover:bg-brand-accent hover:text-white"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Whatever the page wants under the panel — the locations button. */}
        {footer}
      </div>
    </div>
  );
}
